<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import MainLayout from "@/components/layout/MainLayout.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import ResidentStatCard from "@/components/residents/ResidentStatCard.vue";
import ResidentFilterBar from "@/components/residents/ResidentFilterBar.vue";
import ResidentTable from "@/components/residents/ResidentTable.vue";
import ResidentFormModal from "@/components/residents/ResidentFormModal.vue";
import { useResidentStore } from "@/stores/resident";

const router = useRouter();
const residentStore = useResidentStore();
const residentFormModal = ref(null);

const search = computed({
  get: () => residentStore.filters.search,
  set: (value) => residentStore.setSearch(value),
});

const residentType = computed({
  get: () => residentStore.filters.residentType,
  set: (value) => residentStore.setResidentType(value),
});

const paymentStatus = computed({
  get: () => residentStore.filters.paymentStatus,
  set: (value) => residentStore.setPaymentStatus(value),
});

const formatCurrency = (value) => {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 0,
  }).format(value || 0);
};

</script>
<template>
  <MainLayout>
    <div class="container-fluid px-0">
      <PageHeader
        title="รายการลูกบ้าน"
        subtitle="จัดการข้อมูลเจ้าของ ผู้เช่า ข้อมูลติดต่อ และสถานะการชำระค่าส่วนกลาง"
        button-text="เพิ่มลูกบ้าน"
        button-icon="bi-person-plus"
        button-color="dark"
        @action="residentFormModal.open()"
      />

      <div class="row g-4 mb-4">
        <div class="col-md-6 col-xl-3">
          <ResidentStatCard
            title="ลูกบ้านทั้งหมด"
            :value="residentStore.totalResidents.toLocaleString('th-TH')"
            subtitle="จำนวนเจ้าของและผู้เช่าทั้งหมด"
            icon="bi-people"
            color="primary"
          />
        </div>

        <div class="col-md-6 col-xl-3">
          <ResidentStatCard
            title="เจ้าของยูนิต"
            :value="residentStore.ownerCount.toLocaleString('th-TH')"
            subtitle="ผู้ถือกรรมสิทธิ์ในระบบ"
            icon="bi-person-badge"
            color="success"
          />
        </div>

        <div class="col-md-6 col-xl-3">
          <ResidentStatCard
            title="ผู้เช่า"
            :value="residentStore.tenantCount.toLocaleString('th-TH')"
            subtitle="ผู้พักอาศัยแบบผู้เช่า"
            icon="bi-person-workspace"
            color="info"
          />
        </div>

        <div class="col-md-6 col-xl-3">
          <ResidentStatCard
            title="ยอดค้างรวม"
            :value="formatCurrency(residentStore.totalOutstanding)"
            subtitle="ค้างชำระของลูกบ้านทั้งหมด"
            icon="bi-cash-stack"
            color="warning"
          />
        </div>
      </div>

      <ResidentFilterBar
        :search="search"
        :resident-type="residentType"
        :payment-status="paymentStatus"
        :resident-type-options="residentStore.residentTypeOptions"
        :payment-status-options="residentStore.paymentStatusOptions"
        @update:search="search = $event"
        @update:residentType="residentType = $event"
        @update:paymentStatus="paymentStatus = $event"
        @reset="residentStore.resetFilters()"
      />

      <div class="row g-4 mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm rounded-4 bg-info bg-opacity-10">
            <div class="card-body p-4">
              <div
                class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3"
              >
                <div>
                  <div class="fw-bold fs-5 mb-1">
                    ลูกบ้านค้างชำระที่ต้องติดตาม
                  </div>
                  <div class="text-muted">
                    สรุปจำนวนลูกบ้านที่มียอดค้างชำระในรอบปัจจุบัน
                  </div>
                </div>

                <div class="text-lg-end">
                  <div class="display-6 fw-bold text-info-emphasis mb-0">
                    {{ residentStore.overdueCount.toLocaleString("th-TH") }} ราย
                  </div>
                  <div class="text-muted small">สถานะค้างชำระ</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ResidentTable
        :items="residentStore.filteredResidents"
        @view="(resident) => router.push(`/residents/${resident.id}`)"
        @edit="(resident) => residentFormModal.open(resident)"
      />
      <!-- ✅ Mount modal ไว้ที่นี่ -->
      <ResidentFormModal ref="residentFormModal" />
    </div>
  </MainLayout>
</template>
